import type { ReactNode } from "react";
import {
	RadioButton,
	RadioGroup,
} from "@/components/base/radio-buttons/radio-buttons";
import {
	INFRACTION_API_STATUS,
	INFRACTION_DISPLAY_STATUS,
} from "../constants/infraction-api-enums.constant";
import { InfractionStatusBannerCard } from "./infraction-status-banner-card";

export const EDIT_STATUS_OVERRIDE = {
	PAID: String(INFRACTION_API_STATUS.PAID),
	CANCELLED: String(INFRACTION_API_STATUS.CANCELLED),
} as const;

type InfractionEditStatusSectionProps = {
	displayStatus: number;
	statusCode: number;
	statusLabel: string;
	statusOverride: string | null;
	onStatusOverrideChange: (value: string | null) => void;
	error?: string;
};

function StatusOptionCard({ children }: { children: ReactNode }) {
	return (
		<div className="rounded-lg border border-border-primary bg-bg-primary px-5 py-2.5 shadow-xs">
			{children}
		</div>
	);
}

function shouldShowStatusOverrideOptions(
	displayStatus: number,
	statusCode: number
): boolean {
	const isPaid =
		displayStatus === INFRACTION_DISPLAY_STATUS.PAID ||
		statusCode === INFRACTION_API_STATUS.PAID;
	const isCancelled =
		displayStatus === INFRACTION_DISPLAY_STATUS.CANCELLED ||
		statusCode === INFRACTION_API_STATUS.CANCELLED;

	return !(isPaid || isCancelled);
}

export function InfractionEditStatusSection({
	displayStatus,
	statusCode,
	statusLabel,
	statusOverride,
	onStatusOverrideChange,
	error,
}: InfractionEditStatusSectionProps) {
	const showOverrideOptions = shouldShowStatusOverrideOptions(
		displayStatus,
		statusCode
	);

	const handleStatusOptionPress = (optionValue: string) => {
		if (statusOverride === optionValue) {
			onStatusOverrideChange(null);
		}
	};

	return (
		<section className="flex flex-col gap-4">
			<h3 className="font-bold text-fg-primary text-md leading-6">
				Status da Infração
			</h3>

			<InfractionStatusBannerCard
				displayStatus={displayStatus}
				statusCode={statusCode}
				statusLabel={statusLabel}
			/>

			{showOverrideOptions ? (
				<RadioGroup
					aria-label="Selecione o status da infração"
					onChange={(value) => {
						const nextValue = value ? String(value) : null;
						if (nextValue === statusOverride) {
							onStatusOverrideChange(null);
							return;
						}
						onStatusOverrideChange(nextValue);
					}}
					value={statusOverride ?? ""}
				>
					<StatusOptionCard>
						<RadioButton
							hint="Selecione essa opção, caso essa infração tenha sido paga em outro local."
							label="Infração Paga"
							onPress={() => {
								handleStatusOptionPress(EDIT_STATUS_OVERRIDE.PAID);
							}}
							value={EDIT_STATUS_OVERRIDE.PAID}
						/>
					</StatusOptionCard>
					<StatusOptionCard>
						<RadioButton
							hint="Selecione essa opção, caso essa infração tenha sido cancelada em outro local."
							label="Infração Cancelada"
							onPress={() => {
								handleStatusOptionPress(EDIT_STATUS_OVERRIDE.CANCELLED);
							}}
							value={EDIT_STATUS_OVERRIDE.CANCELLED}
						/>
					</StatusOptionCard>
				</RadioGroup>
			) : null}

			{error ? (
				<p className="text-error-primary text-sm" role="alert">
					{error}
				</p>
			) : null}
		</section>
	);
}
